import { Injectable } from '@angular/core';
import {
  InspectionAccessoryDto,
  InspectionOptionRequestDto,
  InspectionReportDto,
  VinDecodeResponseModel,
  ViwInventoryRecord,
  ChromeServiceInputModel,
  EditPostingState,
  SetChromeDataInput
} from 'remarketing-angular-library';
import { Store } from '@ngxs/store';

export class PostingErrorResponse {
  errorHeaderKey: string;
  errorMessageKey: string;
}

@Injectable({ providedIn: 'root' })
export class PostingDataHelperService {

  existingInspectionReport: InspectionReportDto;
  errorHeaderKey: string;
  errorMessageKey: string;
  postingErrorResponse: PostingErrorResponse;
  chromeDataInput: ChromeServiceInputModel = {
    manufacturerModelCode: undefined,
    vehicleTrimName: undefined,
    manufacturerOptionCodes: undefined,
    equipmentDescriptions: undefined,
    exteriorColor: undefined,
    interiorColor: undefined
  };

  constructor(public readonly store: Store) {
  }

  public handleErrorMessaging(error): PostingErrorResponse {
    if (this.errorsExistOnResponse(error)) {
      const errorDetail = error.error?.errors[0]?.code;
      switch (errorDetail) {
        case 'F-REMPLT-IIS-VINACTHLD':
          this.errorHeaderKey = 'textVinDecodeActiveOrHoldErrorHeader';
          this.errorMessageKey = 'textVinDecodeActiveOrHoldErrorMessage';
          break;
        case 'F-REMPLT-IIS-GVIE':
          // This isn't necessarily an error, this is also returned when an inspection is not found for a vehicle.
          this.existingInspectionReport = undefined;
          this.errorHeaderKey = undefined;
          this.errorMessageKey = undefined;
          break;
        case 'F-REMPLT-IIS-VSU':
          // This is returned when VIW cannot be reached or request times out.
          this.existingInspectionReport = undefined;
          this.errorHeaderKey = 'textVIWServiceErrorHeader';
          this.errorMessageKey = 'textVIWServiceErrorMessage';
          break;
        case 'F-REMPLT-IIS-GVOE':
          this.errorHeaderKey = 'textChromeOptionsErrorHeader';
          this.errorMessageKey = 'textChromeOptionsErrorMessage';
          break;
        case 'F-REMPLT-IIS-VINNF':
        case 'F-REMPLT-IIS-INVLDVIN':
          this.errorHeaderKey = 'textVinDecodeNotFoundErrorHeader';
          this.errorMessageKey = 'textVinDecodeNotFoundErrorMessage';
          break;
        case 'F-REMPLT-IIS-GVIV':
          this.errorHeaderKey = 'textVinNotFound';
          this.errorMessageKey = 'textExplanationOfVinNotFound';
          break;
        case 'F-REMPLT-IIS-ASTSVCUNAVL':
        default:
          this.errorHeaderKey = 'textGeneralErrorHeader';
          this.errorMessageKey = 'textGeneralErrorMessage';
          break;
      }
    } else {
      // This is the generic error message. This will occur when calls to IIS fail with no error codes.
      this.errorHeaderKey = 'textGeneralErrorHeader';
      this.errorMessageKey = 'textGeneralErrorMessage';
    }
    this.postingErrorResponse = {
      errorHeaderKey: this.errorHeaderKey,
      errorMessageKey: this.errorMessageKey
    };
    return this.postingErrorResponse;
  }

  errorsExistOnResponse(error): boolean {
    return error?.error?.errors?.length > 0;
  }

  /**
   * Build a request to fetch options from chrome asset service. The request for chrome options requires attributes
   * from both the inspection report and vin decode results if you wish to use an existing inspection report.
   */
  public buildChromeOptionsRequest(useExistingInspection: boolean, manufacturerModelCode?: string, manufacturerOptionCodes?: string[],
                                   existingInspectionReport?: InspectionReportDto): InspectionOptionRequestDto {
    let request;
    const chromeDataFromState = this.store.selectSnapshot(EditPostingState.chromeDataInput);
    let selectedVehicleTrim;
    if (chromeDataFromState?.vehicleTrimName && chromeDataFromState.vehicleTrimName !== 'default') {
     selectedVehicleTrim = {
      vehicleTrimName: chromeDataFromState?.vehicleTrimName
    };
    }
    this.chromeDataInput = chromeDataFromState;
    if (useExistingInspection) {
      const postingData = this.store.selectSnapshot(EditPostingState.updatedPosting);
      if (existingInspectionReport === undefined) {
        existingInspectionReport = postingData?.inventoryItem?.inspectionReport;
      }
      const vehicle = existingInspectionReport?.inspectionData?.vehicle;
      this.chromeDataInput.equipmentDescriptions = vehicle?.accessories;
      this.chromeDataInput.exteriorColor = vehicle?.exteriorColor;
      this.chromeDataInput.interiorColor = vehicle?.interiorColor;
      let accessoriesList;
      if (vehicle && vehicle.accessories) {
        accessoriesList = vehicle.accessories;
      } else {
        accessoriesList = [];
      }
      request = {
        getVehicleOptions: true,
        getInspectionReport: false,
        selectedTrim: selectedVehicleTrim,
        manufacturerModelCode: manufacturerModelCode,
        manufacturerOptionCodes: manufacturerOptionCodes,
        inspectionData: {
          accessories: accessoriesList,
          exteriorColor: vehicle?.exteriorColor,
          interiorColor: vehicle?.interiorColor
        }
      } as InspectionOptionRequestDto;
    } else {
      request = {
        getInspectionReport: false,
        getVehicleOptions: true,
        selectedTrim: selectedVehicleTrim,
        manufacturerModelCode: manufacturerModelCode,
        manufacturerOptionCodes: manufacturerOptionCodes
      } as InspectionOptionRequestDto;
    }
    this.chromeDataInput = {
      ...this.chromeDataInput,
      manufacturerModelCode: manufacturerModelCode,
      manufacturerOptionCodes: manufacturerOptionCodes
    };
    this.store.dispatch(new SetChromeDataInput(this.chromeDataInput));
    return request;
  }

  /**
   * Build the Third Party Posting request object to be sent to Chrome service using the viwInventoryRecord
   * and vinDecodeResult attributes as the inspection data.
   */
  public buildThirdPartyChromeOptionsRequest(viwInventoryRecord?: ViwInventoryRecord, vinDecodeResult?: VinDecodeResponseModel): InspectionOptionRequestDto {
    //update the vin decode vehicle information and viw equipment descriptions in store for calling chrome once trim is changed.
    this.chromeDataInput = this.store.selectSnapshot(EditPostingState.chromeDataInput);
    let selectedVehicleTrim;
    if (this.chromeDataInput?.vehicleTrimName && this.chromeDataInput.vehicleTrimName !== 'default') {
      selectedVehicleTrim = {
        vehicleTrimName: this.chromeDataInput?.vehicleTrimName
      };
    }
    let accessoriesList;
    if (this.chromeDataInput?.equipmentDescriptions) {
      accessoriesList = this.chromeDataInput?.equipmentDescriptions;
    } else {
      accessoriesList = [];
    }
    return {
      getVehicleOptions: true,
      getInspectionReport: false,
      manufacturerModelCode: this.chromeDataInput.manufacturerModelCode,
      manufacturerOptionCodes: this.chromeDataInput.manufacturerOptionCodes,
      selectedTrim: selectedVehicleTrim,
      inspectionData: {
        accessories: accessoriesList,
        exteriorColor: viwInventoryRecord?.exteriorColor,
        interiorColor: viwInventoryRecord?.interiorColor
      }
    } as InspectionOptionRequestDto;
  }

  /**
   * Chrome requires the description field to be populated in the InspectionAccessoryDto.
   * @param accessoryStrings the string array of accessory descriptions from the VIW Inventory record.
   */
  formatAccessoriesForChrome(accessoryStrings: string[]): InspectionAccessoryDto[] {
    const accessoryArray = [] as InspectionAccessoryDto[];
    accessoryStrings?.forEach(accessoryString => accessoryArray.push({ description: accessoryString }));
    return accessoryArray;
  }
}
